import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, TextField, FormControlLabel, Checkbox, Divider, Chip, Alert, Button, IconButton } from '@mui/material';
import InputIcon from '@mui/icons-material/Input';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NumbersIcon from '@mui/icons-material/Numbers';
import ImageIcon from '@mui/icons-material/Image';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { SectionTab } from './tab-button';
import { t } from '../config';

// ── Image input placeholder ──────────────────────────────────────────
const IMAGE_PLACEHOLDER_SRC = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
    <rect width="400" height="400" fill="#f0f2f5" rx="12"/>
    <rect x="110" y="90" width="180" height="145" rx="10" fill="#dde1e7"/>
    <circle cx="158" cy="135" r="22" fill="#f0f2f5"/>
    <polygon points="110,235 168,163 210,205 250,163 290,235" fill="#c8cdd6"/>
    <rect x="140" y="268" width="120" height="14" rx="7" fill="#c8cdd6"/>
    <rect x="160" y="292" width="80" height="10" rx="5" fill="#dde1e7"/>
  </svg>`
)}`;

// ── Date formatting helpers ──────────────────────────────────────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DATE_FORMATS = [
  { key: 'MM-DD-YYYY', label: 'MM-DD-YYYY' },
  { key: 'MMMM-DD-YYYY', label: 'Month Name-DD-YYYY' },
  { key: 'MMMM-DD', label: 'Month Name-DD' },
  { key: 'YYYY', label: 'YYYY' },
  { key: 'MM-DD', label: 'MM-DD' },
];

function formatDate(date, format) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  const monthName = MONTH_NAMES[date.getMonth()];

  switch (format) {
    case 'MM-DD-YYYY':
      return `${mm}-${dd}-${yyyy}`;
    case 'MMMM-DD-YYYY':
      return `${monthName}-${dd}-${yyyy}`;
    case 'MMMM-DD':
      return `${monthName}-${dd}`;
    case 'YYYY':
      return yyyy;
    case 'MM-DD':
      return `${mm}-${dd}`;
    default:
      return `${mm}-${dd}-${yyyy}`;
  }
}

// ── Options list editor ──────────────────────────────────────────────
const OptionsEditor = ({ options, onChange }) => (
  <Box sx={{ ml: 6.5, mr: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    {options.map((opt, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField
          size="small"
          value={opt}
          onChange={(e) => {
            const next = [...options];
            next[i] = e.target.value;
            onChange(next);
          }}
          placeholder={`Option ${i + 1}`}
          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
        <IconButton
          size="small"
          onClick={() => onChange(options.filter((_, j) => j !== i))}
          disabled={options.length <= 1}
          sx={{ color: '#bbb', '&:hover': { color: '#e57373' } }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    ))}
    <Button
      size="small"
      startIcon={<AddIcon />}
      onClick={() => onChange([...options, ''])}
      sx={{ alignSelf: 'flex-start', fontSize: 11, textTransform: 'none', color: '#0d83cd', p: 0.5 }}
    >
      Add option
    </Button>
  </Box>
);

// ── Card component ───────────────────────────────────────────────────
const InputFieldCard = ({ icon, label, description, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(13,131,205,0.06)',
        borderColor: '#0d83cd',
      },
      transition: 'all 0.15s ease',
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '8px',
        backgroundColor: 'rgba(13,131,205,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#0d83cd',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" sx={{ color: '#888', fontSize: 11, lineHeight: 1.2 }}>
          {description}
        </Typography>
      )}
    </Box>
  </Box>
);

// ── Panel content ────────────────────────────────────────────────────
const InputFieldsPanelContent = observer(({ store }) => {
  const [dateFormat, setDateFormat] = useState('MM-DD-YYYY');
  const [textPrompt, setTextPrompt] = useState('');
  const [datePrompt, setDatePrompt] = useState('');
  const [integerPrompt, setIntegerPrompt] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [textRequired, setTextRequired] = useState(false);
  const [dateRequired, setDateRequired] = useState(false);
  const [integerRequired, setIntegerRequired] = useState(false);
  const [imageRequired, setImageRequired] = useState(false);
  const [radioPrompt, setRadioPrompt] = useState('');
  const [radioRequired, setRadioRequired] = useState(false);
  const [radioOptions, setRadioOptions] = useState(['Option 1', 'Option 2']);
  const [listPrompt, setListPrompt] = useState('');
  const [listMinSelections, setListMinSelections] = useState(0);
  const [listMaxSelections, setListMaxSelections] = useState(2);
  const [listOptions, setListOptions] = useState(['Option 1', 'Option 2']);
  const [textRemoveIfSkipped, setTextRemoveIfSkipped] = useState(false);
  const [dateRemoveIfSkipped, setDateRemoveIfSkipped] = useState(false);
  const [integerRemoveIfSkipped, setIntegerRemoveIfSkipped] = useState(false);
  const [imageRemoveIfSkipped, setImageRemoveIfSkipped] = useState(false);
  const [radioRemoveIfSkipped, setRadioRemoveIfSkipped] = useState(false);
  const [listRemoveIfSkipped, setListRemoveIfSkipped] = useState(false);

  const addInputElement = (inputType, extra = {}) => {
    const page = store.activePage;
    if (!page) return;

    const scaleFactor = Math.min(store.width, store.height) / 500;

    if (inputType === 'image') {
      const size = Math.min(store.width, store.height) * 0.3;
      page.addElement({
        type: 'image',
        x: store.width / 2 - size / 2,
        y: store.height / 2 - size / 2,
        width: size,
        height: size,
        src: IMAGE_PLACEHOLDER_SRC,
        custom: {
          inputField: true,
          inputType: 'image',
          placeholder: t('sidePanel.inputFields.imagePlaceholder', 'Upload image'),
          promptText: extra.promptText || t('sidePanel.inputFields.defaultImagePrompt', 'Please upload an image'),
          required: extra.required || false,
          removeIfSkipped: extra.removeIfSkipped || false,
        },
      });
    } else {
      let dummyText = '';
      let placeholder = '';

      let defaultPrompt = '';

      switch (inputType) {
        case 'text':
          dummyText = t('sidePanel.inputFields.sampleText', 'Sample Text');
          placeholder = t('sidePanel.inputFields.textPlaceholder', 'Enter text');
          defaultPrompt = t('sidePanel.inputFields.defaultTextPrompt', 'Please enter your text');
          break;
        case 'date':
          dummyText = formatDate(new Date(), extra.dateFormat || 'MM-DD-YYYY');
          placeholder = t('sidePanel.inputFields.datePlaceholder', 'Select date');
          defaultPrompt = t('sidePanel.inputFields.defaultDatePrompt', 'Please select a date');
          break;
        case 'integer':
          dummyText = '0';
          placeholder = t('sidePanel.inputFields.integerPlaceholder', 'Enter number');
          defaultPrompt = t('sidePanel.inputFields.defaultIntegerPrompt', 'Please enter a number');
          break;
        case 'radio':
          dummyText = (extra.options || []).filter(Boolean)[0] || 'Option 1';
          placeholder = t('sidePanel.inputFields.radioPlaceholder', 'Select an option');
          defaultPrompt = t('sidePanel.inputFields.defaultRadioPrompt', 'Please select an option');
          break;
        case 'list':
          dummyText = (extra.options || []).filter(Boolean).slice(0, 2).join(', ') || 'Option 1, Option 2';
          placeholder = t('sidePanel.inputFields.listPlaceholder', 'Select options');
          defaultPrompt = t('sidePanel.inputFields.defaultListPrompt', 'Please select options');
          break;
        default:
          dummyText = 'Input';
          placeholder = 'Input';
          defaultPrompt = 'Please provide input';
      }

      const fontSize = Math.round(20 * scaleFactor);
      const textWidth = Math.round(250 * scaleFactor);

      page.addElement({
        type: 'text',
        x: store.width / 2 - textWidth / 2,
        y: store.height / 2 - fontSize,
        width: textWidth,
        height: fontSize * 2,
        text: dummyText,
        fontSize: fontSize,
        fontFamily: 'Roboto',
        align: 'center',
        fill: '#333333',
        custom: {
          inputField: true,
          inputType: inputType,
          placeholder: placeholder,
          promptText: extra.promptText || defaultPrompt,
          required: extra.required || false,
          removeIfSkipped: extra.removeIfSkipped || false,
          ...(inputType === 'date' ? { dateFormat: extra.dateFormat || 'MM-DD-YYYY' } : {}),
          ...(['radio', 'list'].includes(inputType) ? { options: (extra.options || []).filter(Boolean) } : {}),
          ...(inputType === 'list' ? { minSelections: extra.minSelections || 0, maxSelections: extra.maxSelections || 0 } : {}),
        },
      });
    }

    store.history.addUndoState();
  };

  return (
    <Box sx={{ p: 2, overflowY: 'auto', height: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 2, color: '#555', fontSize: 13 }}>
        {t('sidePanel.inputFields.title', 'Input Fields')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#999', fontSize: 11 }}>
        {t('sidePanel.inputFields.description', 'Add placeholder elements that require user input when the template is used.')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Text input */}
        <InputFieldCard
          icon={<TextFieldsIcon fontSize="small" />}
          label={t('sidePanel.inputFields.textInput', 'Text Input')}
          description={t('sidePanel.inputFields.textInputDesc', 'Free text entry')}
          onClick={() => addInputElement('text', { promptText: textPrompt, required: textRequired, removeIfSkipped: textRemoveIfSkipped })}
        />
        <TextField
          size="small"
          placeholder={t('sidePanel.inputFields.promptTextHint', 'Prompt text shown to user...')}
          value={textPrompt}
          onChange={(e) => setTextPrompt(e.target.value)}
          sx={{ ml: 6.5, mr: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={textRequired} onChange={(e) => setTextRequired(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.required', 'Required')}</Typography>}
          sx={{ ml: 5.5 }}
        />
        {!textRequired && (
          <FormControlLabel
            control={<Checkbox size="small" checked={textRemoveIfSkipped} onChange={(e) => setTextRemoveIfSkipped(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.removeIfSkipped', 'Remove if skipped')}</Typography>}
            sx={{ ml: 5.5 }}
          />
        )}

        {/* Date input */}
        <InputFieldCard
          icon={<CalendarTodayIcon fontSize="small" />}
          label={t('sidePanel.inputFields.dateInput', 'Date Input')}
          description={t('sidePanel.inputFields.dateInputDesc', 'Date value with format')}
          onClick={() => addInputElement('date', { dateFormat, promptText: datePrompt, required: dateRequired, removeIfSkipped: dateRemoveIfSkipped })}
        />
        <TextField
          size="small"
          placeholder={t('sidePanel.inputFields.promptTextHint', 'Prompt text shown to user...')}
          value={datePrompt}
          onChange={(e) => setDatePrompt(e.target.value)}
          sx={{ ml: 6.5, mr: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={dateRequired} onChange={(e) => setDateRequired(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.required', 'Required')}</Typography>}
          sx={{ ml: 5.5 }}
        />
        {!dateRequired && (
          <FormControlLabel
            control={<Checkbox size="small" checked={dateRemoveIfSkipped} onChange={(e) => setDateRemoveIfSkipped(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.removeIfSkipped', 'Remove if skipped')}</Typography>}
            sx={{ ml: 5.5 }}
          />
        )}

        {/* Date format selector */}
        <FormControl size="small" sx={{ ml: 6.5, mr: 1 }}>
          <InputLabel sx={{ fontSize: 12 }}>
            {t('sidePanel.inputFields.dateFormat', 'Date format')}
          </InputLabel>
          <Select
            value={dateFormat}
            onChange={(e) => setDateFormat(e.target.value)}
            label={t('sidePanel.inputFields.dateFormat', 'Date format')}
            sx={{ fontSize: 12 }}
          >
            {DATE_FORMATS.map((fmt) => (
              <MenuItem key={fmt.key} value={fmt.key} sx={{ fontSize: 12 }}>
                {fmt.label} — {formatDate(new Date(), fmt.key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Integer input */}
        <InputFieldCard
          icon={<NumbersIcon fontSize="small" />}
          label={t('sidePanel.inputFields.integerInput', 'Integer Input')}
          description={t('sidePanel.inputFields.integerInputDesc', 'Numeric value')}
          onClick={() => addInputElement('integer', { promptText: integerPrompt, required: integerRequired, removeIfSkipped: integerRemoveIfSkipped })}
        />
        <TextField
          size="small"
          placeholder={t('sidePanel.inputFields.promptTextHint', 'Prompt text shown to user...')}
          value={integerPrompt}
          onChange={(e) => setIntegerPrompt(e.target.value)}
          sx={{ ml: 6.5, mr: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={integerRequired} onChange={(e) => setIntegerRequired(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.required', 'Required')}</Typography>}
          sx={{ ml: 5.5 }}
        />
        {!integerRequired && (
          <FormControlLabel
            control={<Checkbox size="small" checked={integerRemoveIfSkipped} onChange={(e) => setIntegerRemoveIfSkipped(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.removeIfSkipped', 'Remove if skipped')}</Typography>}
            sx={{ ml: 5.5 }}
          />
        )}

        {/* Image input */}
        <InputFieldCard
          icon={<ImageIcon fontSize="small" />}
          label={t('sidePanel.inputFields.imageInput', 'Image Input')}
          description={t('sidePanel.inputFields.imageInputDesc', 'Image placeholder')}
          onClick={() => addInputElement('image', { promptText: imagePrompt, required: imageRequired, removeIfSkipped: imageRemoveIfSkipped })}
        />
        <TextField
          size="small"
          placeholder={t('sidePanel.inputFields.promptTextHint', 'Prompt text shown to user...')}
          value={imagePrompt}
          onChange={(e) => setImagePrompt(e.target.value)}
          sx={{ ml: 6.5, mr: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={imageRequired} onChange={(e) => setImageRequired(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.required', 'Required')}</Typography>}
          sx={{ ml: 5.5 }}
        />
        {!imageRequired && (
          <FormControlLabel
            control={<Checkbox size="small" checked={imageRemoveIfSkipped} onChange={(e) => setImageRemoveIfSkipped(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.removeIfSkipped', 'Remove if skipped')}</Typography>}
            sx={{ ml: 5.5 }}
          />
        )}

        {/* Radio input */}
        <InputFieldCard
          icon={<RadioButtonCheckedIcon fontSize="small" />}
          label={t('sidePanel.inputFields.radioInput', 'Radio Input')}
          description={t('sidePanel.inputFields.radioInputDesc', 'Single option selection')}
          onClick={() => addInputElement('radio', { promptText: radioPrompt, required: radioRequired, options: radioOptions, removeIfSkipped: radioRemoveIfSkipped })}
        />
        <TextField
          size="small"
          placeholder={t('sidePanel.inputFields.promptTextHint', 'Prompt text shown to user...')}
          value={radioPrompt}
          onChange={(e) => setRadioPrompt(e.target.value)}
          sx={{ ml: 6.5, mr: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={radioRequired} onChange={(e) => setRadioRequired(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.required', 'Required')}</Typography>}
          sx={{ ml: 5.5 }}
        />
        {!radioRequired && (
          <FormControlLabel
            control={<Checkbox size="small" checked={radioRemoveIfSkipped} onChange={(e) => setRadioRemoveIfSkipped(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.removeIfSkipped', 'Remove if skipped')}</Typography>}
            sx={{ ml: 5.5 }}
          />
        )}
        <OptionsEditor options={radioOptions} onChange={setRadioOptions} />

        {/* List (multi-select) input */}
        <InputFieldCard
          icon={<FormatListBulletedIcon fontSize="small" />}
          label={t('sidePanel.inputFields.listInput', 'List Input')}
          description={t('sidePanel.inputFields.listInputDesc', 'Multiple option selection')}
          onClick={() => addInputElement('list', { promptText: listPrompt, minSelections: listMinSelections, maxSelections: listMaxSelections, options: listOptions, removeIfSkipped: listRemoveIfSkipped })}
        />
        <TextField
          size="small"
          placeholder={t('sidePanel.inputFields.promptTextHint', 'Prompt text shown to user...')}
          value={listPrompt}
          onChange={(e) => setListPrompt(e.target.value)}
          sx={{ ml: 6.5, mr: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
        <OptionsEditor options={listOptions} onChange={setListOptions} />
        <Box sx={{ display: 'flex', gap: 1, ml: 6.5, mr: 1 }}>
          <TextField
            size="small"
            type="number"
            label={t('sidePanel.inputFields.minSelections', 'Min select')}
            value={listMinSelections}
            onChange={(e) => setListMinSelections(Math.max(0, parseInt(e.target.value) || 0))}
            inputProps={{ min: 0 }}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
          />
          <TextField
            size="small"
            type="number"
            label={t('sidePanel.inputFields.maxSelections', 'Max select')}
            value={listMaxSelections}
            onChange={(e) => setListMaxSelections(Math.max(0, parseInt(e.target.value) || 0))}
            inputProps={{ min: 0 }}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
          />
        </Box>
        <Typography variant="caption" sx={{ ml: 6.5, color: '#999', fontSize: 10 }}>
          {listMinSelections > 0
            ? `Required · min ${listMinSelections}${listMaxSelections > 0 ? `, max ${listMaxSelections}` : ', no max'}`
            : `Optional · max ${listMaxSelections > 0 ? listMaxSelections : '∞'}`}
        </Typography>
        {listMinSelections === 0 && (
          <FormControlLabel
            control={<Checkbox size="small" checked={listRemoveIfSkipped} onChange={(e) => setListRemoveIfSkipped(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.removeIfSkipped', 'Remove if skipped')}</Typography>}
            sx={{ ml: 5.5 }}
          />
        )}
      </Box>

      {/* ── Mark existing element as variable ─────────────────────── */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#555', fontSize: 13 }}>
        {t('sidePanel.inputFields.markTitle', 'Mark Selected as Variable')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#999', fontSize: 11 }}>
        {t('sidePanel.inputFields.markDescription', 'Convert any selected text or image element into a variable, preserving its existing style.')}
      </Typography>
      <MarkAsVariableForm store={store} />
    </Box>
  );
});

// ── Mark-as-variable sub-form ─────────────────────────────────────────
const MarkAsVariableForm = observer(({ store }) => {
  const [markLabel, setMarkLabel] = useState('');
  const [markRequired, setMarkRequired] = useState(false);
  const [markInputType, setMarkInputType] = useState('text');
  const [markOptions, setMarkOptions] = useState(['Option 1', 'Option 2']);
  const [markMinSelections, setMarkMinSelections] = useState(0);
  const [markMaxSelections, setMarkMaxSelections] = useState(2);
  const [markRemoveIfSkipped, setMarkRemoveIfSkipped] = useState(false);

  const selectedIds = store.selectedElementsIds || [];
  const selectedElement =
    selectedIds.length === 1 ? store.getElementById(selectedIds[0]) : null;

  const isImageElement = selectedElement?.type === 'image';
  const isTextElement = selectedElement?.type === 'text';
  const isSupportedElement = isImageElement || isTextElement;
  const isAlreadyVariable = !!selectedElement?.custom?.inputField;

  useEffect(() => {
    if (selectedElement && isSupportedElement) {
      setMarkLabel(selectedElement.custom?.promptText || '');
      setMarkRequired(!!selectedElement.custom?.required);
      setMarkInputType(
        isImageElement ? 'image' : (selectedElement.custom?.inputType || 'text')
      );
      setMarkOptions(selectedElement.custom?.options || ['Option 1', 'Option 2']);
      setMarkMinSelections(selectedElement.custom?.minSelections || 0);
      setMarkMaxSelections(selectedElement.custom?.maxSelections || 2);
      setMarkRemoveIfSkipped(!!selectedElement.custom?.removeIfSkipped);
    } else {
      setMarkLabel('');
      setMarkRequired(false);
      setMarkInputType('text');
      setMarkOptions(['Option 1', 'Option 2']);
      setMarkMinSelections(0);
      setMarkMaxSelections(2);
      setMarkRemoveIfSkipped(false);
    }
  }, [selectedElement?.id]);

  const handleMark = () => {
    if (!selectedElement || !markLabel.trim()) return;
    const effectiveType = isImageElement ? 'image' : markInputType;
    selectedElement.set({
      custom: {
        ...selectedElement.custom,
        inputField: true,
        promptText: markLabel.trim(),
        inputType: effectiveType,
        required: markRequired,
        removeIfSkipped: !markRequired && markRemoveIfSkipped,
        ...(['radio', 'list'].includes(effectiveType) ? { options: markOptions.filter(Boolean) } : {}),
        ...(effectiveType === 'list' ? { minSelections: markMinSelections, maxSelections: markMaxSelections } : {}),
      },
    });
    store.history.addUndoState();
  };

  const handleUnmark = () => {
    if (!selectedElement) return;
    const {
      inputField: _f,
      promptText: _p,
      inputType: _it,
      required: _r,
      removeIfSkipped: _ris,
      placeholder: _pl,
      options: _o,
      minSelections: _min,
      maxSelections: _max,
      ...rest
    } = selectedElement.custom || {};
    selectedElement.set({ custom: rest });
    store.history.addUndoState();
  };

  if (!selectedElement) {
    return (
      <Typography variant="caption" sx={{ color: '#bbb', display: 'block', textAlign: 'center', py: 1 }}>
        {t('sidePanel.inputFields.noSelection', 'Select a text or image element on the canvas')}
      </Typography>
    );
  }

  if (!isSupportedElement) {
    return (
      <Alert severity="info" sx={{ fontSize: 11, py: 0.5 }}>
        {t('sidePanel.inputFields.unsupportedType', 'Only text and image elements can be marked as variables.')}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {isAlreadyVariable && (
        <Alert severity="success" sx={{ fontSize: 11, py: 0.5 }}>
          {t('sidePanel.inputFields.alreadyVariable', 'Variable')}{': '}
          <strong>{selectedElement.custom.promptText}</strong>
          <Chip label={selectedElement.custom.inputType} size="small" sx={{ ml: 1, fontSize: 10 }} />
        </Alert>
      )}

      <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {t('sidePanel.inputFields.selectedElement', 'Selected')}:{' '}
          <strong style={{ textTransform: 'capitalize' }}>{selectedElement.type}</strong>
        </Typography>
        {isTextElement && selectedElement.text && (
          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: 11, color: '#555' }} noWrap>
            &ldquo;{selectedElement.text}&rdquo;
          </Typography>
        )}
      </Box>

      <TextField
        size="small"
        label={t('sidePanel.inputFields.variableLabel', 'Variable Label')}
        placeholder={t('sidePanel.inputFields.variableLabelHint', 'e.g. First Name, Photo...')}
        value={markLabel}
        onChange={(e) => setMarkLabel(e.target.value)}
        fullWidth
        sx={{ '& .MuiInputBase-input': { fontSize: 12 } }}
      />

      {isTextElement && (
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ fontSize: 12 }}>
            {t('sidePanel.inputFields.inputType', 'Input Type')}
          </InputLabel>
          <Select
            value={markInputType}
            onChange={(e) => setMarkInputType(e.target.value)}
            label={t('sidePanel.inputFields.inputType', 'Input Type')}
            sx={{ fontSize: 12 }}
          >
            <MenuItem value="text" sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.textInput', 'Text Input')}</MenuItem>
            <MenuItem value="integer" sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.integerInput', 'Integer Input')}</MenuItem>
            <MenuItem value="date" sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.dateInput', 'Date Input')}</MenuItem>
            <MenuItem value="radio" sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.radioInput', 'Radio Input')}</MenuItem>
            <MenuItem value="list" sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.listInput', 'List Input')}</MenuItem>
          </Select>
        </FormControl>
      )}

      {isTextElement && (markInputType === 'radio' || markInputType === 'list') && (
        <>
          <Typography variant="caption" sx={{ color: '#888', fontSize: 11 }}>Options</Typography>
          <OptionsEditor options={markOptions} onChange={setMarkOptions} />
        </>
      )}

      {isTextElement && markInputType === 'list' && (
        <>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              type="number"
              label={t('sidePanel.inputFields.minSelections', 'Min select')}
              value={markMinSelections}
              onChange={(e) => setMarkMinSelections(Math.max(0, parseInt(e.target.value) || 0))}
              inputProps={{ min: 0 }}
              sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
            />
            <TextField
              size="small"
              type="number"
              label={t('sidePanel.inputFields.maxSelections', 'Max select')}
              value={markMaxSelections}
              onChange={(e) => setMarkMaxSelections(Math.max(0, parseInt(e.target.value) || 0))}
              inputProps={{ min: 0 }}
              sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: '#999', fontSize: 10 }}>
            {markMinSelections > 0
              ? `Required · min ${markMinSelections}${markMaxSelections > 0 ? `, max ${markMaxSelections}` : ', no max'}`
              : `Optional · max ${markMaxSelections > 0 ? markMaxSelections : '∞'}`}
          </Typography>
        </>
      )}

      {(!isTextElement || markInputType !== 'list') && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={markRequired}
              onChange={(e) => setMarkRequired(e.target.checked)}
            />
          }
          label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.required', 'Required')}</Typography>}
        />
      )}

      {!markRequired && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={markRemoveIfSkipped}
              onChange={(e) => setMarkRemoveIfSkipped(e.target.checked)}
            />
          }
          label={<Typography sx={{ fontSize: 12 }}>{t('sidePanel.inputFields.removeIfSkipped', 'Remove if skipped')}</Typography>}
        />
      )}

      <Button
        variant="contained"
        size="small"
        fullWidth
        disabled={!markLabel.trim()}
        onClick={handleMark}
        sx={{ textTransform: 'none', fontSize: 12 }}
      >
        {isAlreadyVariable
          ? t('sidePanel.inputFields.updateVariable', 'Update Variable')
          : t('sidePanel.inputFields.markAsVariable', 'Mark as Variable')}
      </Button>

      {isAlreadyVariable && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          fullWidth
          onClick={handleUnmark}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {t('sidePanel.inputFields.removeVariable', 'Remove Variable')}
        </Button>
      )}
    </Box>
  );
});

export const InputFieldsSection = {
  name: 'inputFields',
  Tab: (props) => (
    <SectionTab name={t('sidePanel.inputFields.tab', 'Input Fields')} {...props}>
      <InputIcon />
    </SectionTab>
  ),
  Panel: InputFieldsPanelContent,
};
